import React, { useState, useEffect, useMemo } from "react";
import config from "../../Config";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import PrintHeader from "../General/PrintHeader";
import Page from "../General/Page";
import RightsCheck from "../General/RightsCheck";

const PrintTrialBalanceLevelWise = () => {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));

  const [loading, setLoading] = useState(false);
  const [listOfLedger, setLedger] = useState([]);
  const [OpeningDebitTotal, setOpeningDebitTotal] = useState("");
  const [OpeningCreditTotal, setOpeningCreditTotal] = useState("");
  const [CurrentDebitTotal, setCurrentDebitTotal] = useState("");
  const [CurrentCreditTotal, setCurrentCreditTotal] = useState("");
  const [ClosingDebitTotal, setClosingDebitTotal] = useState("");
  const [ClosingCreditTotal, setClosingCreditTotal] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);

  const filters = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const legacyLevel = urlParams.get("Accountlevel");
    return {
      fyear:
        urlParams.get("fyear") ||
        localStorage.getItem("DefaultFYear") ||
        "",
      startDate: urlParams.get("startDate") || "",
      endDate: urlParams.get("endDate") || "",
      accountLevel:
        urlParams.get("accountLevel") ||
        legacyLevel ||
        "3",
      showZeroAccounts: urlParams.get("showZeroAccounts") !== "0",
      accountCode: (urlParams.get("accountCode") || urlParams.get("AccountCode") || "").trim(),
      accountName: (urlParams.get("accountName") || urlParams.get("AccountName") || "").trim(),
    };
  }, []);

  useEffect(() => {
    document.title = "Print Trial Balance Level Wise";
    setLoading(true);

    const data = {
      AccessKey: AccessKey,
      UserID: UserID,
      MemberType: MemberType,
      pageNo: 1,
      pageSize: 10000,
      FYear: parseInt(filters.fyear, 10) || 0,
      StartDate: filters.startDate,
      EndDate: filters.endDate,
      AccountLevel: parseInt(filters.accountLevel, 10) || 0,
      ShowZeroAccounts: filters.showZeroAccounts,
      AccountCode: filters.accountCode,
      AccountName: filters.accountName,
    };

    axios({
      method: "post",
      url: config.base_url + "Accounts/GetTrialBalance",
      headers: { "Content-Type": "application/json" },
      data: data,
    })
      .then((response) => {
        if (response.data?.status_code !== 0) {
          setLedger(
            Array.isArray(response.data?.ListofRecords)
              ? response.data.ListofRecords
              : []
          );
          setTotalRecords(response.data?.totalRecords ?? 0);
          setOpeningDebitTotal(response.data?.OpeningDebitTotal ?? "");
          setOpeningCreditTotal(response.data?.OpeningCreditTotal ?? "");
          setCurrentDebitTotal(response.data?.CurrentDebitTotal ?? "");
          setCurrentCreditTotal(response.data?.CurrentCreditTotal ?? "");
          setClosingDebitTotal(response.data?.ClosingDebitTotal ?? "");
          setClosingCreditTotal(response.data?.ClosingCreditTotal ?? "");
        } else {
          setLedger([]);
          setTotalRecords(0);
        }
      })
      .catch(() => {
        setLedger([]);
        setTotalRecords(0);
      })
      .finally(() => setLoading(false));
  }, [AccessKey, UserID, MemberType, filters]);

  const renderLedger = () => {
    if (listOfLedger != null && listOfLedger.length > 0) {
      return listOfLedger.map((item, index) => (
        <tr key={index}>
          <td>{item.Selected_Account.Account_Code}</td>
          <td>{item.Selected_Account.Account_Description}</td>
          <td className="text-right">{item.OpeningDebit}</td>
          <td className="text-right">{item.OpeningCredit}</td>
          <td className="text-right">{item.CurrentDebit}</td>
          <td className="text-right">{item.CurrentCredit}</td>
          <td className="text-right">{item.ClosingDebit}</td>
          <td className="text-right">{item.ClosingCredit}</td>
        </tr>
      ));
    }

    return (
      <tr>
        <td colSpan="8">
          <h4
            className="mt-3"
            style={{ fontSize: "15px", color: "rgba(0,0,0,0.6)" }}
          >
            No Data Found
          </h4>
        </td>
      </tr>
    );
  };

  const levelLabel =
    filters.accountLevel === "1"
      ? "1st Level"
      : filters.accountLevel === "2"
      ? "2nd Level"
      : filters.accountLevel === "3"
      ? "3rd Level"
      : "All Levels";

  return (
    <>
      <RightsCheck />
      <Page orientation="portrait" size="a4">
        <table className="table table-sm table-bordered table-print">
          <thead>
            <tr>
              <th colSpan={20}>
                <PrintHeader title="Trial Balance Level Wise" />
              </th>
            </tr>
            <tr>
              <th colSpan={20}>
                Year: {filters.fyear} | Level: {levelLabel} | Period:{" "}
                {filters.startDate || "—"} to {filters.endDate || "—"} | Zero
                accounts: {filters.showZeroAccounts ? "Yes" : "No"} | Records:{" "}
                {totalRecords}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="text-center" colSpan="8">
                  <Spinner animation="border" role="status"></Spinner>
                </td>
              </tr>
            ) : (
              <>
                <tr>
                  <th>Account Code</th>
                  <th>Account Name</th>
                  <th colSpan="2" className="text-center">
                    Opening
                  </th>
                  <th colSpan="2" className="text-center">
                    Current
                  </th>
                  <th colSpan="2" className="text-center">
                    Closing
                  </th>
                </tr>
                <tr>
                  <th></th>
                  <th></th>
                  <th className="text-center">Debit</th>
                  <th className="text-center">Credit</th>
                  <th className="text-center">Debit</th>
                  <th className="text-center">Credit</th>
                  <th className="text-center">Debit</th>
                  <th className="text-center">Credit</th>
                </tr>
                {renderLedger()}
                <tr>
                  <td></td>
                  <td className="text-end fw-bold">Total</td>
                  <td className="text-right fw-bold">{OpeningDebitTotal}</td>
                  <td className="text-right fw-bold">{OpeningCreditTotal}</td>
                  <td className="text-right fw-bold">{CurrentDebitTotal}</td>
                  <td className="text-right fw-bold">{CurrentCreditTotal}</td>
                  <td className="text-right fw-bold">{ClosingDebitTotal}</td>
                  <td className="text-right fw-bold">{ClosingCreditTotal}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </Page>
    </>
  );
};

export default PrintTrialBalanceLevelWise;
